/**
 * Artificial Turf / Fake Grass Installation Cost Database
 *
 * Real installed turf pricing swings a lot by crew, base depth, yard access,
 * and local supply, so this model OUTPUTS RANGES (low/high per sq ft), never
 * false-precision point values. Every figure is a national ballpark; each
 * region carries labor + material multipliers so the engine can nudge the
 * national base rates by roughly the same +/-15-20% the lawn-care cost model
 * uses. Keys mirror REGIONAL_LAWN_COSTS so the same
 * getRegionFromZipCode -> toLegacyRegion fallback resolves a region.
 *
 * Do not treat these as quotes. The tool copy hedges every dollar figure and
 * nudges the user to get local quotes.
 */

export type TurfGrade = 'economy' | 'standard' | 'premium'
export type TurfInstallMethod = 'diy' | 'professional'

export interface TurfCostRange {
  low: number
  high: number
}

/**
 * Turf material only (the synthetic grass itself), per sq ft. Higher grades
 * add face weight, better backing, and longer warranties.
 */
export const TURF_MATERIAL_COSTS: Record<TurfGrade, TurfCostRange> = {
  economy: { low: 2.0, high: 3.0 },
  standard: { low: 3.5, high: 5.0 },
  premium: { low: 5.5, high: 8.0 },
}

/** Aggregate sub-base (class II road base / decomposed granite) + weed barrier, per sq ft. */
export const TURF_BASE_PREP_COST: TurfCostRange = { low: 1.5, high: 3.0 }

/** Infill (silica sand and/or antimicrobial coated sand), per sq ft. */
export const TURF_INFILL_COST: TurfCostRange = { low: 0.4, high: 1.0 }

/** Professional installation labor (demo prep, grading, compaction, seaming, nailing, infill), per sq ft. */
export const TURF_PRO_LABOR_COST: TurfCostRange = { low: 4.0, high: 8.0 }

/** Removing and hauling the existing lawn before install, per sq ft (labor-heavy). */
export const TURF_REMOVAL_COST: TurfCostRange = { low: 1.0, high: 2.5 }

/**
 * DIY tool + rental amount per sq ft (plate compactor rental, turf/sod cutter
 * rental, seaming tape, glue, and staples/nails). No labor line: the sweat
 * equity is yours. Small relative to material, but not free.
 */
export const TURF_DIY_TOOL_COST: TurfCostRange = { low: 0.25, high: 0.6 }

/**
 * When a DIYer removes the old lawn themselves it is disposal + a sod-cutter
 * rental, not crew labor, so it lands well below the pro removal line. Modeled
 * as a fraction of TURF_REMOVAL_COST.
 */
export const DIY_REMOVAL_FRACTION = 0.45

export interface RegionalTurfCosts {
  region: string
  /** Scales the labor-heavy lines (professional install, pro lawn removal). */
  laborMultiplier: number
  /** Scales the material-driven lines (turf, base, infill, DIY tools). Freight + local supply. */
  materialMultiplier: number
}

/**
 * Keyed to the same region set as REGIONAL_LAWN_COSTS so the engine can fall
 * back through toLegacyRegion() for the fine-grained climate sub-regions
 * (California -> West Coast, Florida -> Southeast, etc.). Multipliers loosely
 * track the labor-rate ordering in the lawn-care cost model.
 */
export const REGIONAL_TURF_COSTS: Record<string, RegionalTurfCosts> = {
  Northeast: { region: 'Northeast', laborMultiplier: 1.1, materialMultiplier: 1.05 },
  Southeast: { region: 'Southeast', laborMultiplier: 0.88, materialMultiplier: 0.95 },
  Midwest: { region: 'Midwest', laborMultiplier: 0.95, materialMultiplier: 0.98 },
  Southwest: { region: 'Southwest', laborMultiplier: 1.0, materialMultiplier: 1.0 },
  West: { region: 'West', laborMultiplier: 1.18, materialMultiplier: 1.08 },
  'Pacific Northwest': { region: 'Pacific Northwest', laborMultiplier: 1.12, materialMultiplier: 1.05 },
  'Mid-Atlantic': { region: 'Mid-Atlantic', laborMultiplier: 1.04, materialMultiplier: 1.02 },
  'Great Lakes': { region: 'Great Lakes', laborMultiplier: 0.98, materialMultiplier: 1.0 },
  'Upper Midwest': { region: 'Upper Midwest', laborMultiplier: 0.92, materialMultiplier: 0.97 },
  Central: { region: 'Central', laborMultiplier: 0.9, materialMultiplier: 0.96 },
  'South Central': { region: 'South Central', laborMultiplier: 0.9, materialMultiplier: 0.95 },
  'Mountain West': { region: 'Mountain West', laborMultiplier: 1.08, materialMultiplier: 1.05 },
  'West Coast': { region: 'West Coast', laborMultiplier: 1.2, materialMultiplier: 1.1 },
}

/** Human-friendly labels for the three turf grades. */
export const TURF_GRADE_LABELS: Record<TurfGrade, string> = {
  economy: 'Economy',
  standard: 'Standard',
  premium: 'Premium',
}
